/**
 * Who the signed-in user may see — story 0.5 AC-2.
 *
 * Two sources, deliberately, because they answer different questions:
 *
 *  - **the session says WHICH** (`Sessao.clientes`). Story 0.3 put that list on the
 *    session and documented that 0.5 consumes it. It is authorization-shaped data,
 *    and it never comes from a token claim.
 *  - **the API says WHAT THEY ARE CALLED** (`GET /eu` on API User, mocked in 0.6).
 *    A display name is not authorization and has no business gating anything.
 *
 * 🔒 The intersection is what the selector offers, and the session is the one that
 * narrows: if the API returns a client the session does not list, it is NOT shown.
 * That ordering matters — the reverse would let a mock, or a future API bug, widen
 * a user's visible tenants.
 *
 * ⚠️ BR-17: this is UX. The server is what actually refuses cross-tenant data.
 */
import { CLIENTES_STUB } from '@/tenant/clientesStub'

export interface ClienteVisivel {
  idCliente: string
  nome: string
}

/**
 * Resolves the selector's options.
 *
 * `nomesConhecidos` is whatever the API could tell us. A client the session grants
 * but the API has not named still appears, under its id — losing access to a tenant
 * because its label failed to load would be a worse failure than an ugly label.
 */
export function clientesVisiveis(
  daSessao: readonly string[],
  nomesConhecidos: readonly ClienteVisivel[] = [],
): ClienteVisivel[] {
  const porId = new Map(nomesConhecidos.map((cliente) => [cliente.idCliente, cliente.nome]))
  // The stub carries the handoff's own tenant names (shot `2n`) and is the fallback
  // until the real endpoint exists.
  for (const stub of CLIENTES_STUB) {
    if (!porId.has(stub.idCliente)) porId.set(stub.idCliente, stub.nome)
  }

  return daSessao.map((idCliente) => ({
    idCliente,
    nome: porId.get(idCliente) ?? idCliente,
  }))
}

/** Whether the user may enter this tenant at all — story 0.5 AC-7. */
export function podeAcessar(idCliente: string, daSessao: readonly string[]): boolean {
  return daSessao.includes(idCliente)
}
